// src/services/retryService.ts
// Wrapper service for common operations with automatic retry

import { retryWithBackoff, handleError, AppError, ErrorMessages, isOnline, waitForConnection } from './errorHandler';
import { supabase } from './supabase';

/**
 * Upload file with retry logic
 */
export async function uploadFileWithRetry(
  bucket: string,
  path: string,
  file: Blob,
  options?: any
): Promise<{ success: boolean; data?: any; error?: AppError }> {
  // Check if online first
  if (!isOnline()) {
    return {
      success: false,
      error: handleError({ message: ErrorMessages.NETWORK_OFFLINE }),
    };
  }

  return retryWithBackoff(async () => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, options);

    if (error) throw error;
    return data;
  }, 3, 1000);
}

/**
 * Send message with retry logic
 */
export async function sendMessageWithRetry(
  messageFn: () => Promise<any>
): Promise<{ success: boolean; data?: any; error?: AppError }> {
  // Check if online first
  if (!isOnline()) {
    // Wait up to 5 seconds for connection
    const connected = await waitForConnection(5000);
    if (!connected) {
      return {
        success: false,
        error: handleError({ message: ErrorMessages.NETWORK_OFFLINE }),
      };
    }
  }

  return retryWithBackoff(messageFn, 2, 1000);
}

/**
 * Fetch data with retry logic
 */
export async function fetchDataWithRetry<T>(
  fetchFn: () => Promise<T>
): Promise<{ success: boolean; data?: T; error?: AppError }> {
  // Check if online first
  if (!isOnline()) {
    return {
      success: false,
      error: handleError({ message: ErrorMessages.NETWORK_OFFLINE }),
    };
  }

  return retryWithBackoff(fetchFn, 2, 1500);
}

/**
 * Update data with retry logic
 */
export async function updateDataWithRetry<T>(
  updateFn: () => Promise<T>
): Promise<{ success: boolean; data?: T; error?: AppError }> {
  // Check if online first
  if (!isOnline()) {
    return {
      success: false,
      error: handleError({ message: ErrorMessages.NETWORK_OFFLINE }),
    };
  }

  return retryWithBackoff(updateFn, 2, 1000);
}

/**
 * Generic operation with custom retry config
 */
export async function executeWithRetry<T>(
  operation: () => Promise<T>,
  retries: number = 3,
  delayMs: number = 1000
): Promise<{ success: boolean; data?: T; error?: AppError }> {
  return retryWithBackoff(operation, retries, delayMs);
}
